import { useJourneysStore } from '@/shared/store/useJourneysStore'
import {
  FilterChips,
  ListSection,
  PageHeader,
  PageHero,
  Screen
} from '@/shared/ui/Layout'

import { JourneyListCard } from './components/JourneyListCard'
import { JourneysListEmpty } from './components/JourneysListEmpty'
import { JourneysListSkeleton } from './components/JourneysListSkeleton'
import { humanName } from './config'
import type { JourneyFilterId } from './constants/status'
import { useJourneysListView } from './hooks/useJourneysListView'
import { DigitalProductJourneysShow } from './Show'

export const DigitalProductJourneys = () => {
  const { journeys: rawJourneys } = useJourneysStore()
  const {
    filter,
    setFilter,
    visibleFilters,
    groups,
    items,
    globalProgress,
    isEmpty,
    isFilteredEmpty,
    isLoading
  } = useJourneysListView()

  if (rawJourneys.length === 1) {
    return <DigitalProductJourneysShow id={rawJourneys[0].uid} />
  }

  return (
    <Screen>
      <Screen.Context className="mb-4">
        <PageHeader title={humanName.plural} />

        {!isEmpty && (
          <PageHero
            art="/journeys/backpack.png"
            backgroundImage="/journeys/hero-bg.jpg"
            className="mb-0"
          >
            <PageHero.Title>Sua jornada no evento</PageHero.Title>
            <PageHero.ProgressRow
              layout="stacked"
              value={globalProgress.percent}
              label={`${globalProgress.completed} de ${globalProgress.total} concluídas`}
            />
          </PageHero>
        )}
      </Screen.Context>

      <Screen.Sticky>
        {/* `visibleFilters`, não a lista completa: chip sem item correspondente
            não é renderizado. */}
        <FilterChips<JourneyFilterId>
          options={visibleFilters}
          value={filter}
          onChange={setFilter}
          aria-label="Filtrar jornadas"
        />
      </Screen.Sticky>

      <Screen.Content className="pt-4">
        {isLoading && <JourneysListSkeleton />}

        {!isLoading && isEmpty && <JourneysListEmpty />}

        {!isLoading && isFilteredEmpty && (
          <JourneysListEmpty filtered onClearFilter={() => setFilter('all')} />
        )}

        {/* Em "Todas", seções por status na ordem canônica — a listagem era
            plana e era a única sem separação. Com filtro específico a lista
            volta a ser plana: todos os itens têm o mesmo status.
            Ritmo por `gap-*` no container, nunca `mb-*` no filho (MASTER L4). */}
        {!isLoading && !isEmpty && !isFilteredEmpty && filter === 'all' && (
          <div className="flex flex-col gap-5">
            {groups.map(group => (
              <ListSection
                key={group.status}
                id={`journeys-section-${group.status}`}
                title={group.label}
                count={group.items.length}
                status={group.status}
              >
                {group.items.map(journey => (
                  <JourneyListCard key={journey.id} journey={journey} />
                ))}
              </ListSection>
            ))}
          </div>
        )}

        {!isLoading && !isEmpty && !isFilteredEmpty && filter !== 'all' && (
          <div className="flex flex-col gap-3">
            {items.map(journey => (
              <JourneyListCard key={journey.id} journey={journey} />
            ))}
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <p className="px-0 pb-10 pt-5 text-center text-2xs font-semibold tracking-wide text-gray-700">
            Todas as jornadas carregadas
          </p>
        )}
      </Screen.Content>
    </Screen>
  )
}
